// src/components/profile/ProfileHeader.js
import { useTranslation } from 'react-i18next';

const ProfileHeader = ({ user, onEdit }) => {
  const { t } = useTranslation();
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-600 dark:bg-red-700';
      case 'journalist':
        return 'bg-blue-600 dark:bg-blue-700';
      default:
        return 'bg-gray-600 dark:bg-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return t('roles.admin', 'Administrator');
      case 'journalist':
        return t('dashboard.journalistTeam');
      default:
        return t('roles.user', 'User');
    }
  };

  const getStatusBadge = () => {
    if (!user.is_active) {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
          Deactivated
        </span>
      );
    }
    if (!user.email_verified) {
      return (
        <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
          Unverified
        </span>
      );
    }
    return (
      <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
        Active
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg p-6 border border-shayek-light-300 dark:border-shayek-dark-200">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          {/* Avatar */}
          <div className="relative">
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.name || user.email}
                className="w-24 h-24 rounded-full object-cover border-4 border-shayek-light-200 dark:border-shayek-dark-300"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-shayek-primary to-shayek-primary-600 flex items-center justify-center text-white text-3xl font-bold border-4 border-shayek-light-200 dark:border-shayek-dark-300">
                {(user.name || user.email || 'U').charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* User Info */}
          <div className="flex-1">
            <p className="text-xs font-medium text-shayek-light-500 dark:text-shayek-dark-500 mb-1">{t('common.appName')}</p>
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900">
                {user.name || 'No Name Set'}
              </h1>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full text-white ${getRoleBadgeColor(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
              {getStatusBadge()}
            </div>
            <p className="text-shayek-light-600 dark:text-shayek-dark-600 mb-2">{user.email}</p>
            {user.bio && (
              <p className="text-shayek-light-700 dark:text-shayek-dark-700 text-sm mb-2">{user.bio}</p>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-shayek-light-600 dark:text-shayek-dark-600">
              {user.phone && (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {user.phone}
                </span>
              )}
              {user.location && (
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {user.location}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Edit Button */}
        <button
          onClick={onEdit}
          className="px-4 py-2 bg-shayek-primary dark:bg-shayek-primary-600 text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 transition-colors"
        >
          Edit Profile
        </button>
      </div>
    </div>
  );
};

export default ProfileHeader;

